import { AuthApiError } from '../errors.js';
import { noRefreshCredentialStore, } from './refresh-credential-store.js';
/**
 * Builds a typed client bound to a specific API base. The returned object is
 * stateless aside from delegating to `getToken` / `setToken`; safe to memoize
 * once per app.
 */
export function createAuthClient(options) {
    const apiBase = stripTrailingSlash(options.apiBase);
    const refreshCredentials = options.refreshCredentials ?? noRefreshCredentialStore();
    async function request(path, requestOptions = {}) {
        const { method = 'GET', body, searchParams, auth = false, skipRenewal = false, signal } = requestOptions;
        const url = buildUrl(apiBase, path, searchParams);
        const headers = new Headers({ Accept: 'application/json' });
        if (auth) {
            // Renew on use, before the request rather than after its refusal (`ADR 051` §2). With no
            // renewal wired the expression collapses to today's behaviour: whatever `getToken` says.
            const held = options.getToken();
            const token = skipRenewal || options.credentialFitToSend === undefined
                ? held
                : await options.credentialFitToSend(held);
            if (token !== null && token.length > 0) {
                headers.set('Authorization', `Bearer ${token}`);
            }
        }
        let serializedBody;
        if (body !== undefined) {
            headers.set('Content-Type', 'application/json');
            serializedBody = JSON.stringify(body);
        }
        const response = await fetch(url, {
            method,
            headers,
            cache: 'no-store',
            ...(serializedBody === undefined ? {} : { body: serializedBody }),
            ...(signal === undefined ? {} : { signal }),
        });
        const responseText = await response.text();
        if (!response.ok) {
            const envelope = parseErrorEnvelope(responseText, response.status);
            // Treat 401 on authed requests as the "session ended" signal so the host
            // can redirect to login. We deliberately do not auto-clear the token
            // here — the provider does that on logout/refresh-failure.
            if (response.status === 401 && auth) {
                options.onUnauthenticated?.();
            }
            throw new AuthApiError(envelope, response.status);
        }
        if (responseText === '')
            return undefined;
        try {
            return JSON.parse(responseText);
        }
        catch {
            throw new AuthApiError({ errorCode: 'INVALID_RESPONSE', message: 'The server returned data that was not valid JSON.' }, response.status);
        }
    }
    /**
     * Persists **both** credentials from a fresh `LoginResult` and returns the result for
     * convenience. Centralised so every login-shaped endpoint behaves identically.
     *
     * The refresh credential is the half that was missing. `captureLogin` used to be one line —
     * `options.setToken(result.sessionToken)` — and the server's refresh token, which was on the
     * wire the whole time, ended here. That is survey finding 1: nothing renewed, because nothing
     * had kept the thing renewal needs.
     *
     * Guarded on a non-empty string rather than trusted, even though the type says it is always
     * there: this is the boundary where a JSON body becomes a typed value, and the type is a claim
     * about the server, not a check on it.
     */
    function captureLogin(result) {
        options.setToken(result.sessionToken);
        if (typeof result.refreshToken === 'string' && result.refreshToken.length > 0) {
            refreshCredentials.write(result.refreshToken);
        }
        return result;
    }
    /** Ends the session, scoped by whether a refresh credential is sent. See {@link AuthClient.logout}. */
    async function endSession(scope) {
        const refreshToken = refreshCredentials.read();
        try {
            await request('/auth/logout', {
                method: 'POST',
                // **The body is the scope.** `flow-adapter.ts` revokes exactly the refresh token it is
                // given and falls back to `GlobalSignOutCommand` when it is given none — and because
                // this client sent `body: {}`, every sign-out this platform has ever performed took the
                // fallback. Signing out on a laptop ended the session on the phone. `scope` is sent
                // alongside so "everywhere" is a thing the client *asks for* rather than the accident of
                // an absent field; see the route.
                body: scope === 'everywhere' ? { scope } : { scope, ...(refreshToken === null ? {} : { refreshToken }) },
                auth: true,
                skipRenewal: true,
            });
        }
        finally {
            // Always clear local state — even if the server call fails the user's
            // intent is "be logged out", and a stale token will fail every
            // subsequent request anyway. The refresh credential goes with it, or the
            // next request would renew the session the user just ended.
            options.setToken(null);
            refreshCredentials.clear();
        }
    }
    return {
        async signup(input) {
            const response = await request('/auth/signup', { method: 'POST', body: input });
            if (response.result !== undefined)
                captureLogin(response.result);
            return response;
        },
        async login(input) {
            const outcome = await request('/auth/login', { method: 'POST', body: input });
            // `mfa_enrolment_required` also carries a session — and persisting it is load-bearing:
            // that token is what authorises the enrolment endpoints the SPA is about to call.
            if (outcome.status === 'authenticated' || outcome.status === 'mfa_enrolment_required') {
                captureLogin(outcome.result);
            }
            return outcome;
        },
        async initiateOtpLogin(input) {
            return request('/auth/login/otp/initiate', { method: 'POST', body: input });
        },
        async completeOtpLogin(input) {
            const outcome = await request('/auth/login/otp/complete', { method: 'POST', body: input });
            if (outcome.status === 'authenticated')
                captureLogin(outcome.result);
            return outcome;
        },
        async completeMfa(input) {
            const result = await request('/auth/login/mfa/complete', { method: 'POST', body: input });
            return captureLogin(result);
        },
        async beginTotpEnrolment() {
            return request('/auth/mfa/totp/begin', { method: 'POST', body: {}, auth: true });
        },
        async completeTotpEnrolment(input) {
            await request('/auth/mfa/totp/complete', { method: 'POST', body: input, auth: true });
        },
        async disableTotp() {
            await request('/auth/mfa/totp/disable', { method: 'POST', body: {}, auth: true });
        },
        async logout() {
            await endSession('device');
        },
        async logoutEverywhere() {
            await endSession('everywhere');
        },
        async refreshSession() {
            const refreshToken = refreshCredentials.read();
            if (refreshToken === null) {
                // Nothing to renew *with*. Raised as the same refusal the server would give, so a caller
                // has one error shape to handle and the renewer's "the session is over" branch does not
                // have to special-case an empty store.
                throw new AuthApiError({ errorCode: 'AUTHENTICATION_REQUIRED', message: 'Sign in to continue.' }, 401);
            }
            // **In the body, never the header.** `public-auth-router.ts` reads `refreshToken` from the
            // body and only falls back to the bearer for legacy Kratos callers — its own comment says
            // Cognito answers `NotAuthorized` to the bearer. This client sent `body: {}` with the
            // bearer, so the one call that could have renewed a session would have failed on the
            // provider the platform actually runs. A body also keeps a 30-day credential out of proxy
            // access logs, which an `Authorization` header is not.
            const result = await request('/auth/session/refresh', {
                method: 'POST',
                body: { refreshToken },
                skipRenewal: true,
            });
            return captureLogin(result);
        },
        async initiatePasswordRecovery(input) {
            return request('/auth/password/recover/initiate', { method: 'POST', body: input });
        },
        async completePasswordRecovery(input) {
            const result = await request('/auth/password/recover/complete', { method: 'POST', body: input });
            return captureLogin(result);
        },
        async inspectPasswordResetLink({ token }) {
            return request('/users/password-reset-link/inspect', {
                method: 'POST',
                body: { token },
            });
        },
        async completePasswordResetLink({ token, newPassword }) {
            return request('/users/password-reset-link/complete', {
                method: 'POST',
                body: { token, password: newPassword },
            });
        },
        async changePassword(input) {
            await request('/auth/password/change', { method: 'POST', body: input, auth: true });
        },
        async initiateEmailVerification(input) {
            return request('/auth/email/verify/initiate', { method: 'POST', body: input });
        },
        async completeEmailVerification(input) {
            await request('/auth/email/verify/complete', { method: 'POST', body: input });
        },
        async getMe() {
            return request('/auth/me', { auth: true });
        },
        async updateProfile(input) {
            return request('/auth/me', { method: 'PATCH', body: input, auth: true });
        },
        async getMfaSettings() {
            return request('/auth/me/mfa', { auth: true });
        },
        async updateMfaSettings(input) {
            await request('/auth/me/mfa', { method: 'PUT', body: input, auth: true });
        },
        async listSocialProviders() {
            return request('/auth/social/providers');
        },
        async startSocialLogin({ provider, returnTo }) {
            const path = `/auth/social/${encodeURIComponent(provider)}/start`;
            return request(path, {
                searchParams: { return_to: returnTo },
            });
        },
        async startSocialSignup({ provider, returnTo }) {
            const path = `/auth/social/${encodeURIComponent(provider)}/signup-start`;
            return request(path, {
                searchParams: { return_to: returnTo },
            });
        },
        async exchangeSocialLogin(input) {
            const result = await request('/auth/social/exchange', {
                method: 'POST',
                body: input,
            });
            // captureLogin persists the bearer token to localStorage AND React state. Without
            // this, the OIDC callback page navigates to / before the token is set →
            // ProtectedRoute sees user=null → bounces back to /login. Easy to forget when
            // adding new login-shaped endpoints.
            return captureLogin(result);
        },
        async inspectLinkFlow({ flowId }) {
            return request('/auth/link/inspect', { searchParams: { flowId } });
        },
        async submitLinkPassword(input) {
            const result = await request('/auth/link/password', {
                method: 'POST',
                body: input,
            });
            return captureLogin(result);
        },
        async initiateLinkCode(input) {
            await request('/auth/link/code/initiate', { method: 'POST', body: input });
        },
        async completeLinkCode(input) {
            const result = await request('/auth/link/code/complete', {
                method: 'POST',
                body: input,
            });
            return captureLogin(result);
        },
        // -- Admin -----------------------------------------------------------------
        async adminListUsers(listOptions = {}) {
            return request('/admin/users', {
                auth: true,
                searchParams: { limit: listOptions.limit, cursor: listOptions.cursor },
            });
        },
        async adminGetUser(id) {
            return request(`/admin/users/${encodeURIComponent(id)}`, { auth: true });
        },
        async adminCreateUser(input) {
            return request('/admin/users', { method: 'POST', body: input, auth: true });
        },
        async adminUpdateUser(id, input) {
            return request(`/admin/users/${encodeURIComponent(id)}`, {
                method: 'PATCH',
                body: input,
                auth: true,
            });
        },
        async adminDeleteUser(id) {
            await request(`/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE', auth: true });
        },
        async adminGetUserGroups(id) {
            return request(`/admin/users/${encodeURIComponent(id)}/groups`, { auth: true });
        },
        async adminSetUserGroups(id, groupNames) {
            return request(`/admin/users/${encodeURIComponent(id)}/groups`, {
                method: 'PUT',
                body: { groupNames },
                auth: true,
            });
        },
        async adminAddUserGroup(id, group) {
            return request(`/admin/users/${encodeURIComponent(id)}/groups/${encodeURIComponent(group)}`, { method: 'POST', auth: true });
        },
        async adminRemoveUserGroup(id, group) {
            return request(`/admin/users/${encodeURIComponent(id)}/groups/${encodeURIComponent(group)}`, { method: 'DELETE', auth: true });
        },
        async adminResetUserPassword(id) {
            await request(`/admin/users/${encodeURIComponent(id)}/password/reset`, {
                method: 'POST',
                body: {},
                auth: true,
            });
        },
    };
}
function buildUrl(apiBase, path, searchParams) {
    const url = new URL(path.startsWith('/') ? path.slice(1) : path, `${apiBase}/`);
    if (searchParams !== undefined) {
        for (const [key, value] of Object.entries(searchParams)) {
            if (value === undefined)
                continue;
            url.searchParams.set(key, String(value));
        }
    }
    return url.toString();
}
function stripTrailingSlash(value) {
    return value.endsWith('/') ? value.slice(0, -1) : value;
}
/**
 * Best-effort decode of the API's `{ errorCode, message }` envelope. Falls back
 * to a generic shape so callers always get an `AuthApiError` they can branch on
 * by HTTP status when the body is unparseable.
 */
function parseErrorEnvelope(responseText, httpStatus) {
    if (responseText === '') {
        return { errorCode: fallbackCodeForStatus(httpStatus), message: fallbackMessageForStatus(httpStatus) };
    }
    try {
        const parsed = JSON.parse(responseText);
        const errorCode = typeof parsed.errorCode === 'string' && parsed.errorCode.length > 0
            ? parsed.errorCode
            : fallbackCodeForStatus(httpStatus);
        const message = typeof parsed.message === 'string' && parsed.message.length > 0
            ? parsed.message
            : typeof parsed.error === 'string' && parsed.error.length > 0
                ? parsed.error
                : fallbackMessageForStatus(httpStatus);
        return { errorCode, message };
    }
    catch {
        return { errorCode: fallbackCodeForStatus(httpStatus), message: fallbackMessageForStatus(httpStatus) };
    }
}
function fallbackCodeForStatus(status) {
    if (status === 401)
        return 'AUTHENTICATION_REQUIRED';
    if (status === 403)
        return 'PERMISSION_DENIED';
    if (status === 404)
        return 'NOT_FOUND';
    if (status === 422)
        return 'VALIDATION_ERROR';
    if (status >= 500)
        return 'SERVER_ERROR';
    return 'REQUEST_FAILED';
}
function fallbackMessageForStatus(status) {
    if (status === 401)
        return 'Sign in to continue.';
    if (status === 403)
        return 'You do not have permission to perform that action.';
    if (status === 404)
        return 'The requested resource was not found.';
    if (status >= 500)
        return 'The server is unavailable. Try again in a moment.';
    return `Request failed with status ${status}.`;
}
//# sourceMappingURL=auth-client.js.map